import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { signedFetch } from '../../super-picker/services/api';
import { greetingName } from '../../super-picker/components/share-links/shareLinkFormat';
import {
  SendToClientsAdapter,
  SendToClientsSheet,
  SheetClient,
  SheetTemplate,
} from './sendToClients/SendToClientsSheet';

export type ShareAssetKind = 'edm' | 'ebook' | 'report';

/** Asset payload for share menus (EDM / eBook / report). */
export interface ShareEmailAsset {
  id: string;
  kind: ShareAssetKind;
  title: string;
  filename?: string | null;
  url: string;
  /** Project the card was opened from, used to preselect the Custom message. */
  projectId?: string | null;
}

export type ShareWhatsAppAsset = ShareEmailAsset;

function shareKindLabel(kind: ShareAssetKind | undefined): string {
  if (kind === 'ebook') return 'eBook';
  if (kind === 'report') return 'Report';
  return 'EDM';
}

const COLD_LEAD_MESSAGE = `Hi {{client_name}}, this is {{agent_name}}!

I thought you might find this interesting:
{{edm_link}}

Feel free to take a look, and let me know if you'd like to find out more. Happy to share more details with you!`;

const WARM_LEAD_MESSAGE = `Hi {{client_name}}!

I thought I'd share this with you, as it might be relevant to what we've been discussing:
{{edm_link}}

Have a look when you have a chance! Let me know if you'd like to discuss it further, and I'd be happy to walk you through the details.

{{agent_name}}`;

const BASE_TEMPLATES: SheetTemplate[] = [
  { key: 'cold', label: 'Cold Leads', body: COLD_LEAD_MESSAGE },
  { key: 'warm', label: 'Warm Leads', body: WARM_LEAD_MESSAGE },
];

const PLACEHOLDERS = [
  { token: '{{client_name}}', label: 'Client name' },
  { token: '{{agent_name}}', label: 'My name' },
  { token: '{{edm_link}}', label: 'Link' },
];

const LINK_TOKEN = /\{\{\s*edm_link\s*\}\}/i;

function fillAssetMessage(template: string, vars: { clientName: string; agentName: string; link: string }): string {
  return template
    .replace(/\{\{\s*client_name\s*\}\}/gi, vars.clientName)
    .replace(/\{\{\s*agent_name\s*\}\}/gi, vars.agentName)
    .replace(/\{\{\s*edm_link\s*\}\}/gi, vars.link);
}

interface ConnectorTag { Id: string | number; Name: string; Color?: string | null }
interface ConnectorClient { Id: string | number; Name: string; Mobile?: string | null; Tags?: ConnectorTag[] | null }
interface TaggedShareProject { projectId: string; projectName: string; message: string }
/** One client's tracked link for this asset, created on the server. */
type Minted = { trackUrl: string; agentName: string };

async function readJson<T>(res: Response): Promise<T & { error?: string }> {
  return (await res.json().catch(() => ({}))) as T & { error?: string };
}

const CLIENTS_PAGE_SIZE = 500;

/** Every Super Connector client the agent has; the list endpoint is paged. */
async function loadAllClients(): Promise<ConnectorClient[]> {
  const all: ConnectorClient[] = [];
  for (;;) {
    const res = await signedFetch(`/api/super-connector/clients?limit=${CLIENTS_PAGE_SIZE}&offset=${all.length}`);
    if (!res.ok) throw new Error('Failed to load Super Connector clients');
    const data = await readJson<{ clients?: ConnectorClient[]; total?: number }>(res);
    const page = data.clients || [];
    all.push(...page);
    const total = typeof data.total === 'number' ? data.total : all.length;
    if (page.length < CLIENTS_PAGE_SIZE || all.length >= total) {
      // Pages are ordered by UpdatedAt, so a client edited mid-load can appear twice.
      return Array.from(new Map(all.map((c) => [String(c.Id), c])).values());
    }
  }
}

/**
 * Custom templates from the project share messages, following the same rules
 * as before: one per tagged project that has a message (the card's project
 * first), or the untagged generic message when the file has no project.
 */
function customTemplates(projects: TaggedShareProject[], generic: string, preferredProjectId?: string | null): SheetTemplate[] {
  const withMessage = projects.filter((p) => p.message.trim());
  if (withMessage.length === 0) {
    return projects.length === 0 && generic.trim() ? [{ key: 'custom', label: 'Custom', body: generic }] : [];
  }
  if (withMessage.length === 1) return [{ key: 'custom', label: 'Custom', body: withMessage[0].message }];
  const ordered = [...withMessage].sort((a, b) => Number(b.projectId === preferredProjectId) - Number(a.projectId === preferredProjectId));
  return ordered.map((p) => ({ key: `custom-${p.projectId}`, label: `Custom · ${p.projectName}`, body: p.message }));
}

/**
 * "Send to my clients" for an EDM, eBook or report. Each client gets their own
 * tracked link (per client, per file), so opens show in that client's
 * Super Connector activity.
 */
export default function ShareAssetToClientsSheet({ open, onClose, asset }: {
  open: boolean;
  onClose: () => void;
  asset: ShareEmailAsset | null;
}) {
  const [custom, setCustom] = useState<SheetTemplate[]>([]);
  const [minted, setMinted] = useState<Record<string, Minted>>({});
  const mintRequests = useRef(new Map<string, Promise<Minted>>());
  const assetId = asset?.id ?? '';

  // Links are per client per file: drop them when the sheet opens for another file.
  useEffect(() => {
    mintRequests.current = new Map();
    setMinted({});
  }, [assetId]);

  const preferredProjectId = asset?.projectId ?? null;
  useEffect(() => {
    if (!open || !assetId) return undefined;
    let cancelled = false;
    setCustom([]);
    void (async () => {
      try {
        const res = await signedFetch(`/api/project-share-messages/for-asset?edmId=${encodeURIComponent(assetId)}`);
        const payload = await readJson<{ projects?: TaggedShareProject[]; genericMessage?: string }>(res);
        if (!res.ok) throw new Error(payload.error || 'Failed to load project messages');
        if (cancelled) return;
        const projects = Array.isArray(payload.projects) ? payload.projects : [];
        const generic = typeof payload.genericMessage === 'string' ? payload.genericMessage : '';
        setCustom(customTemplates(projects, generic, preferredProjectId));
      } catch {
        if (!cancelled) setCustom([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, assetId, preferredProjectId]);

  const mint = useCallback((client: SheetClient): Promise<Minted> => {
    const existing = mintRequests.current.get(client.id);
    if (existing) return existing;
    const request = (async () => {
      const res = await signedFetch('/api/super-connector/media/share-whatsapp-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edmId: assetId, clientId: client.id }),
      });
      const payload = await readJson<{ trackUrl?: string; agentName?: string }>(res);
      if (!res.ok || !payload.trackUrl) throw new Error(payload.error || 'Failed to create tracked link');
      const result = { trackUrl: payload.trackUrl, agentName: (payload.agentName || '').trim() };
      setMinted((prev) => ({ ...prev, [client.id]: result }));
      return result;
    })();
    mintRequests.current.set(client.id, request);
    // A failed request is not kept, so Retry asks the server again.
    request.catch(() => { if (mintRequests.current.get(client.id) === request) mintRequests.current.delete(client.id); });
    return request;
  }, [assetId]);

  const subtitle = `${asset?.title || asset?.filename || 'File'} · ${shareKindLabel(asset?.kind)}`;
  const adapter = useMemo((): SendToClientsAdapter => {
    const anyMinted = Object.values(minted)[0];
    return {
      subtitle,
      load: async () => {
        const [allClients, tagsRes] = await Promise.all([
          loadAllClients(),
          signedFetch('/api/super-connector/tags'),
        ]);
        const tags = tagsRes.ok ? await readJson<ConnectorTag[]>(tagsRes) : [];
        const colorById = new Map((Array.isArray(tags) ? tags : []).map((t) => [String(t.Id), t.Color ?? null]));
        const clients = allClients
          .filter((c) => typeof c.Mobile === 'string' && c.Mobile.trim().length > 0)
          .map((c): SheetClient => ({
            id: String(c.Id),
            name: (c.Name || '').trim(),
            mobile: (c.Mobile || '').trim(),
            tags: (c.Tags || []).map((t) => ({
              id: String(t.Id),
              name: t.Name,
              color: t.Color ?? colorById.get(String(t.Id)) ?? null,
            })),
            lastSentAt: null,
          }));
        return { clients, agentName: '' };
      },
      templates: [...BASE_TEMPLATES, ...custom],
      placeholders: PLACEHOLDERS,
      linkToken: LINK_TOKEN,
      preview: (message, client, agentName) => {
        const link = client ? minted[client.id] : undefined;
        return fillAssetMessage(message, {
          clientName: client ? greetingName(client.name) : '{{client_name}}',
          agentName: link?.agentName || anyMinted?.agentName || agentName || '{{agent_name}}',
          link: link?.trackUrl || (client ? 'Creating link…' : '{{edm_link}}'),
        });
      },
      prepare: async (client, message) => {
        const link = await mint(client);
        const text = fillAssetMessage(message, {
          clientName: greetingName(client.name),
          agentName: link.agentName,
          link: link.trackUrl,
        });
        const res = await signedFetch('/api/super-connector/whatsapp/deep-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId: client.id, message: text }),
        });
        const payload = await readJson<{ deepLink?: string }>(res);
        if (!res.ok || !payload.deepLink) throw new Error(payload.error || 'Failed to build WhatsApp link');
        return { href: payload.deepLink, text };
      },
      recordSend: async (client, text) => {
        // Non-critical: a failed log must not sign the agent out.
        const res = await signedFetch('/api/super-connector/whatsapp/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId: client.id, messageBody: text }),
        }, true, false);
        if (!res.ok) throw new Error('Failed to record the send');
        return undefined;
      },
      warm: (client) => { void mint(client).catch(() => undefined); },
      emptyHint: 'Only Super Connector clients with a mobile number are listed here.',
      doneHint: 'Each client has their own tracked link, so opens show in their Super Connector activity.',
    };
  }, [subtitle, custom, minted, mint]);

  return (
    <SendToClientsSheet
      open={open && !!asset}
      resetKey={assetId}
      adapter={adapter}
      onClose={onClose}
    />
  );
}
